import { useState } from "react";

import { CircularProgress } from "@/components/ui/circular-progress";
import { IconButton } from "@/components/ui/icon-button";
import { MuiKeyboardArrowDownIcon, MuiKeyboardArrowUpIcon } from "@/components/ui/mui-arrow-icons";
import { MuiCollapse } from "@/components/ui/mui-collapse";
import {
  hasSpecUsage,
  type CatalogDetailItem,
  type CatalogDetailRow,
} from "@/lib/catalog-item-detail";
import { cn } from "@/lib/utils";
import { MuiTypography } from "@/components/ui/mui-typography";
import { MUI_TYPOGRAPHY_VARIANTS } from "@/components/ui/mui-typography-shape";

// The details page's fact table — `Details/Details.tsx` — and the
// `Details/SpecUsage.tsx` rows the donor renders *into* it.
//
// **Two components sharing one `<tbody>`, which is the donor's own shape.**
// `Details` takes `children` and drops them in after the dimension rows
// (`Details.tsx:41`), and the island fills that slot with
// `{!isPublic && <SpecUsage .../>}` (`ItemDetailsIsland.tsx:181-183`). So the
// usage row is not a part of the table — it is a guest in it, and the gate on
// the guest is the whole of how modsy keeps spec counts off the anonymous
// product page.
//
// They stay in one file, and `ItemSpecUsageRows` returns bare `<tr>`s rather
// than a table of its own, because the alignment is the point: the usage row
// shares the table's columns and its 60%/40% split. A second table would give
// it columns of its own and the labels would stop lining up — the kind of drift
// that only shows up on a screenshot.
//
// The cell shape is the donor's `DetailTableCell`: `paddingLeft: 0`, a 1px
// bottom border in `theme.palette.divider` (MUI's default `rgba(0,0,0,0.12)`,
// which is `--color-lennar-divider`), and `width: '60%'`. It is page-local
// geometry rather than a shared donor component — `DetailTableCell` is declared
// inside `Details.tsx` and has no other call site.

/** One division's usage, as the breakdown renders it. */
export interface CatalogDivisionUsage {
  divisionId: number;
  usageCount: number;
  /** The division's name, resolved by Python through `divisions` (ADR-0013). */
  name: string;
}

/**
 * What the Total Specifications value cell is showing.
 *
 * The donor drives this from a *second* query (`useGetItemUsageFalconQuery`);
 * here the counts ride the one detail read, so the caller passes the state of
 * that read. The three rendered arms are the donor's — spinner, the literal
 * word "Error", the count. There is no empty arm before the first expand, and
 * no spinner on expand: a named parity break (#554 D1, owner-ruled
 * 2026-09-30). Since timmy `c701607d` the donor's cell is empty until the row
 * is expanded (`SpecUsage.tsx:108`, `!hasRequested ? null : …`) and spins on
 * every expand; Atlas shows the count as soon as the page or dialog draws and
 * keeps it. The donor's full behaviour is in the phase-1 parity ledger, §2.
 */
export type ItemUsageState = "loading" | "error" | "ready";

interface ItemDetailTableProps {
  /**
   * The rows to print, already ordered and already filtered — `detailRows` or
   * `detailRowsFromFacts` (`@/lib/catalog-item-detail`).
   *
   * Empty is a rendered state, not a skipped one: the donor's `isLoading` is
   * false on error (`ItemDetailsIsland.tsx:126`), so the table still draws its
   * heading and an empty body under the error snackbar. Standing there empty is
   * the donor's answer, and a more honest one than a skeleton that never
   * resolves.
   */
  rows: readonly CatalogDetailRow[];
  /**
   * The donor's `children` slot, after the dimension rows (`Details.tsx:41`).
   *
   * The authed page fills it with `ItemSpecUsageRows`; the anonymous product
   * page passes nothing, which is `{!isPublic && ...}` rendering `false`.
   */
  children?: React.ReactNode;
}

/**
 * The donor's `DetailTableCell`, minus the width it only sets on the label.
 *
 * 16px of vertical padding is MUI's `TableCell` default, which
 * `DetailTableCell` (`Details/Details.tsx:69-76`) overrides only on the left —
 * so it is what every row in this table stands at. Twelve looks close in a
 * diff and costs about 80px of table height across the page.
 *
 * `body2` for the same reason: `TableCell`'s root style *is* `body2`
 * (`@mui/material/TableCell/TableCell.js:48`), so both columns are 14px in the
 * donor. The `Typography variant='body2'` the label cell wraps its text in
 * restates what the cell already says — it is not emphasis, and reading it as
 * emphasis leaves every value on this page a step larger than modsy's.
 *
 * The shared shape, not a bare `text-sm`: `body2`'s line height is 1.43, so
 * 20.02px, where `text-sm`'s is 20px. Two hundredths a row is nothing on one
 * row and most of a pixel down a long breakdown — item 8103 stood 1px shorter
 * than modsy on it (#553 D6).
 */
const CELL = `border-lennar-divider border-b py-4 pr-4 pl-0 align-middle ${MUI_TYPOGRAPHY_VARIANTS.body2}`;

export function ItemDetailTable({ rows, children }: ItemDetailTableProps) {
  return (
    <>
      {/* `subtitle1` with `mt: 2, mb: 1`, as the section's `h2`. */}
      <MuiTypography variant="subtitle1" component="h2" className="mt-4 mb-2">
        Details
      </MuiTypography>
      {/* `auto`, as MUI's `Table` leaves it: the 60% below is a hint a browser
          may overrule for a long value, and fixing the layout makes it a hard
          column that wraps values the donor does not. */}
      <table data-testid="pdp-details-table" className="w-full border-collapse">
        <tbody>
          {rows.map(({ label, value }) => (
            <tr key={label} data-testid={`detail-row-${label}`}>
              {/* The 60% is on the label cell alone: the donor sets it on the
                  shared cell component, so both cells claim it and the first
                  one wins the layout. Stating it once says the same thing
                  without the puzzle. */}
              <td className={cn(CELL, "w-[60%]")}>{label}</td>
              <td className={CELL}>{value}</td>
            </tr>
          ))}

          {children}
        </tbody>
      </table>
    </>
  );
}

interface ItemSpecUsageRowsProps {
  /**
   * The item, or `null` when the read has failed — the donor still draws the
   * row, reading zero (`ItemDetailsIsland.tsx:126`).
   */
  item: CatalogDetailItem | null;
  /**
   * Specifications of `type = 'core'` using the item — the breakdown's Core
   * row, drawn first and only when above zero. Beside `item` rather than on it
   * because it is on the detail read alone (`coreUsageCount`).
   */
  coreUsageCount?: number;
  divisionUsage: readonly CatalogDivisionUsage[];
  /**
   * Whether the displayed item's breakdown has arrived at all. The donor reads
   * its counts from a cache a sibling enters with none (`spec_usage=None`), so
   * until the sibling's own read lands the page does not know that nothing
   * uses it — and says nothing rather than "Not used in any specifications".
   */
  breakdownArrived?: boolean;
  usageState?: ItemUsageState;
  /**
   * The breakdown was opened — the moment the donor's usage read fires
   * (`SpecUsage.tsx:74-86`), which every card kebab for the item then shares.
   */
  onExpand?: () => void;
}

/**
 * `Details/SpecUsage.tsx` — the Total Specifications row and the division
 * breakdown it opens into.
 *
 * **Authed only.** The donor gates the whole block on `!isPublic`
 * (`ItemDetailsIsland.tsx:182`), so an anonymous visitor never learns how many
 * specifications use an item; the anonymous product page simply does not mount
 * this.
 *
 * Returns two sibling `<tr>`s with no wrapper, so it lands in the caller's
 * `<tbody>` and inherits its columns — see the note at the top of this file.
 */
export function ItemSpecUsageRows({
  item,
  coreUsageCount = 0,
  divisionUsage,
  breakdownArrived = true,
  usageState = "ready",
  onExpand,
}: ItemSpecUsageRowsProps) {
  const [expanded, setExpanded] = useState(false);
  // `SpecUsage.tsx:53-60` collapses on every `itemId` change — a variant chip
  // swaps the item underneath this row, and the open breakdown belonged to the
  // one that was showing. Reset during render off the remembered id, React's
  // own answer to "reset state when a prop changes".
  const [expandedFor, setExpandedFor] = useState(item?.id);
  if (expandedFor !== item?.id) {
    setExpandedFor(item?.id);
    setExpanded(false);
  }
  // `{!hasSpecUsage && !isError && …}` (`SpecUsage.tsx:119-125`): the one
  // row an item nothing uses opens onto. Only over counts that have actually
  // arrived and read cleanly — a failed read has shown nothing about usage, so
  // it lists only what it holds, which the donor's cache does too.
  const showNoUsageRow =
    breakdownArrived &&
    usageState === "ready" &&
    item !== null &&
    !hasSpecUsage(item, coreUsageCount);
  // The donor's `DivisionRow` list: Core first when core specifications use the
  // item (`coreSpecCnt > 0`, labelled via `defaultDivisionName`), then the
  // divisions in wire order — Python's, which is modsy's name order. One list
  // so the last-row rule below spans both, as the donor's `isLast` does.
  const breakdown = [
    ...(coreUsageCount > 0
      ? [{ key: "core", testId: "core-usage-row", label: "Core", count: coreUsageCount }]
      : []),
    ...divisionUsage.map(({ divisionId, name, usageCount }) => ({
      key: `division-${divisionId}`,
      testId: `division-usage-row-${divisionId}`,
      label: name,
      count: usageCount,
    })),
  ];

  return (
    <>
      <tr data-testid="detail-row-total-specifications">
        {/* The usage row's label cell alone takes `py: 1.3` (`SpecUsage
                .tsx:61`); its value cell keeps the table's 16px, so the row
                still stands as tall as every other one. */}
        <td className={cn(CELL, "w-[60%] py-[10.4px]")}>
          <span className="flex flex-row items-center gap-2">
            <span>Total Specifications</span>
            {/* Whatever the usage (`SpecUsage.tsx:98-104`, since timmy
                    `c701607d`; #553 D1): an item nothing uses opens onto a row
                    that says so. `IconButton size='small'` around the `SvgIcon`
                    default 24px arrow — 34px square — named by what a press
                    will do. */}
            <IconButton
              type="button"
              size="small"
              aria-label={expanded ? "collapse row" : "expand row"}
              // Beyond the donor, and kept by owner ruling (#666 D2).
              aria-expanded={expanded}
              onClick={() => {
                if (!expanded) onExpand?.();
                setExpanded(!expanded);
              }}
            >
              {expanded ? <MuiKeyboardArrowUpIcon /> : <MuiKeyboardArrowDownIcon />}
            </IconButton>
          </span>
        </td>
        <td className={CELL} data-testid="total-specifications-value">
          {usageState === "loading" ? (
            // The donor's `CircularProgress size={20}` (`SpecUsage.tsx:108`),
            // not the branded `Loader`, which is a 15rem Lottie for a whole
            // list. No `color` there either, so it takes the component's
            // `primary.main` default.
            <CircularProgress size={20} data-testid="total-specifications-spinner" />
          ) : usageState === "error" ? (
            // The donor prints this literal word in the cell
            // (`SpecUsage.tsx:108`) — the dismissible snackbar carries the
            // sentence, and this stays a value in a table.
            "Error"
          ) : (
            (item?.usageCount ?? 0)
          )}
        </td>
      </tr>

      {/* The row itself is always in the table (`SpecUsage.tsx:111-112`) — it
              is what carries the hairline under Total Specifications — while
              its *contents* come and go, `Collapse in timeout='auto'
              unmountOnExit` — slid open and shut, then gone. Keeping
              the contents out of the document while collapsed matters for more
              than weight: a hidden row inside a table is exactly the thing a
              screen reader reads out anyway. */}
      <tr>
        <td colSpan={2} className={cn(CELL, "p-0")}>
          {/* `in={open && … && !isBusy}` (`SpecUsage.tsx:115`): shut until the
              count beside it has resolved, so a breakdown is never drawn under
              a total that is still spinning. */}
          <MuiCollapse in={expanded && usageState !== "loading"}>
            <table data-testid="division-usage-rows" className="w-full border-collapse">
              <tbody>
                {/* One cell and no count, borderless, ahead of the list — which
                    is empty whenever this row draws (`SpecUsage.tsx:120-124`).
                    The `Typography body2` is kept here, unlike the label cells
                    above: it restates the cell's look, but it is the paragraph
                    node modsy's row hands a screen reader. */}
                {showNoUsageRow && (
                  <tr data-testid="no-usage-row">
                    <td className={cn(CELL, "w-[60%] border-b-0")}>
                      <MuiTypography variant="body2">Not used in any specifications</MuiTypography>
                    </td>
                  </tr>
                )}
                {breakdown.map(({ key, testId, label, count }, index) => {
                  const last = index === breakdown.length - 1;
                  const cell = cn(CELL, last && "border-b-0");
                  return (
                    <tr key={key} data-testid={testId}>
                      <td className={cn(cell, "w-[60%]")}>{label}</td>
                      <td className={cell}>{count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </MuiCollapse>
        </td>
      </tr>
    </>
  );
}
